import { getExtensionValue } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";

import { CurrentAndPreviousQuestionnaireResponses } from "@/encounters/types/procedures";
import { Extensions } from "@/fhir/enums/extensions";

/**
 * One procedure code answer as the viewed amendment left it, paired with the version it replaced.
 *
 * Each procedure code version records the encounter version it was saved under. The previous side
 * is the newest version saved under an earlier amendment. When the viewed amendment saved versions,
 * its newest one is the current side. When the viewed amendment left the procedure alone, the
 * previous side is still in effect, so it fills both sides and the card shows it as unchanged. A version with no recorded encounter version was saved before amendments tracked it,
 * so it counts as in effect for every amendment. A procedure first saved by a later amendment has
 * neither side.
 *
 * Removing a procedure saves a `stopped` version that keeps the encounter version of the save before
 * it, so a removal is placed on the newest encounter version saved at or before the removal. A
 * `stopped` version means the procedure does not exist on that side: the amendment that removed it
 * shows only the previous side, and later amendments leave it out.
 *
 * @param procedureCodeVersions - One procedure code QuestionnaireResponse's history, newest first
 * @param viewedVersionId - The encounter QuestionnaireResponse version the page is showing
 * @param encounterVersions - Every encounter QuestionnaireResponse version, newest first
 * @returns Both sides of the diff, each undefined when the procedure did not exist on that side
 */
export function resolveProcedureCodeVersionPair(
  procedureCodeVersions: QuestionnaireResponse[],
  viewedVersionId: string,
  encounterVersions: QuestionnaireResponse[],
): CurrentAndPreviousQuestionnaireResponses {
  const attributedVersionIds = procedureCodeVersions.map((procedureCodeVersion) =>
    getAttributedEncounterVersionId(procedureCodeVersion, encounterVersions),
  );

  const viewedEncounterVersionIndex = encounterVersions.findIndex(
    (encounterVersion) => encounterVersion.meta?.versionId === viewedVersionId,
  );
  const earlierEncounterVersionIds = new Set(
    viewedEncounterVersionIndex === -1
      ? []
      : encounterVersions
          .slice(viewedEncounterVersionIndex + 1)
          .map((encounterVersion) => encounterVersion.meta?.versionId),
  );
  const previousVersionIndex = attributedVersionIds.findIndex(
    (attributedVersionId) =>
      attributedVersionId === undefined || earlierEncounterVersionIds.has(attributedVersionId),
  );
  const previousVersion = getExistingVersion(procedureCodeVersions[previousVersionIndex]);

  const savedByViewedVersionIndex = attributedVersionIds.indexOf(viewedVersionId);
  if (savedByViewedVersionIndex !== -1) {
    return {
      currentQuestionnaireResponse: getExistingVersion(
        procedureCodeVersions[savedByViewedVersionIndex],
      ),
      previousQuestionnaireResponse: previousVersion,
    };
  }

  return {
    currentQuestionnaireResponse: previousVersion,
    previousQuestionnaireResponse: previousVersion,
  };
}

/**
 * The encounter QuestionnaireResponse version a procedure code version's change belongs to.
 *
 * @param procedureCodeVersion - One version of a procedure code QuestionnaireResponse
 * @param encounterVersions - Every encounter QuestionnaireResponse version, newest first
 * @returns The recorded parent version id for a save; for a removal, the id of the newest encounter
 * version saved at or before it, falling back to the recorded parent version id
 */
function getAttributedEncounterVersionId(
  procedureCodeVersion: QuestionnaireResponse,
  encounterVersions: QuestionnaireResponse[],
): string | undefined {
  const parentVersionId = getParentVersionId(procedureCodeVersion);
  const removedAt = procedureCodeVersion.meta?.lastUpdated;
  if (procedureCodeVersion.status !== "stopped" || !removedAt) return parentVersionId;

  const encounterVersionAtRemoval = encounterVersions.find(
    (encounterVersion) =>
      encounterVersion.meta?.lastUpdated && encounterVersion.meta.lastUpdated <= removedAt,
  );
  return encounterVersionAtRemoval?.meta?.versionId ?? parentVersionId;
}

/**
 * The encounter QuestionnaireResponse version a procedure code version was saved under.
 *
 * @param procedureCodeVersion - One version of a procedure code QuestionnaireResponse
 * @returns The parent version id, or undefined when the version does not record one
 */
function getParentVersionId(procedureCodeVersion: QuestionnaireResponse): string | undefined {
  const parentVersionId = getExtensionValue(
    procedureCodeVersion,
    Extensions.ParentQuestionnaireResponseVersionId,
  );
  return typeof parentVersionId === "string" ? parentVersionId : undefined;
}

/**
 * A procedure code version, or undefined when the version records the procedure's removal.
 *
 * @param procedureCodeVersion - One version of a procedure code QuestionnaireResponse, if any
 * @returns The version, or undefined when it is missing or `stopped`
 */
function getExistingVersion(
  procedureCodeVersion: QuestionnaireResponse | undefined,
): QuestionnaireResponse | undefined {
  return procedureCodeVersion?.status === "stopped" ? undefined : procedureCodeVersion;
}
